export const isPendingTransaction = (status: string): boolean =>
  String(status || '').toLowerCase() === 'pending';

export const getPaymentMode = (
  paymentMethodName: string,
  cardCompany?: string | null,
  cardType?: string | null
): string => {
  if (!paymentMethodName) return 'Unknown payment type';
  const safeCompany = cardCompany?.trim() || '';
  const safeType = cardType?.trim() || '';
  if (paymentMethodName === 'Credit Card')
    return safeCompany ? `${safeCompany} ${safeType} card`.trim() : 'Credit Card';
  if (paymentMethodName === 'Debit Card')
    return safeCompany ? `${safeCompany} ${safeType} card`.trim() : 'Debit Card';
  if (paymentMethodName === 'preAuthorizedDebit') return 'Pre Authorized Debit';
  return paymentMethodName;
};

/**
 * A successful payment is only "Completed" once funds have been sent to the landlord,
 * i.e. expectedPaymentSendDate is today or in the past. Until then it is still processing.
 */
export const isAwaitingLandlordSend = (
  status: string,
  expectedPaymentSendDate?: string | null
): boolean => {
  if (String(status || '').toLowerCase() !== 'success' || !expectedPaymentSendDate) return false;
  const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD in local time
  return expectedPaymentSendDate.slice(0, 10) > today;
};

/** A refund is stored as a successful `credit` transaction (money returned to the tenant). */
export const isRefundTransaction = (transaction: {
  transactionType?: string | null;
  status?: string | null;
}): boolean =>
  String(transaction.transactionType || '').toLowerCase() === 'credit' &&
  String(transaction.status || '').toLowerCase() === 'success';

export const REFUND_STATUS_CONFIG = {
  label: 'Refunded',
  colorClass: 'text-[#0055FF]',
  bgColorClass: 'bg-[rgba(0,85,255,0.08)]',
};

export const getStatusConfig = (
  status: string,
  expectedPaymentSendDate?: string | null
): { label: string; colorClass: string; bgColorClass: string } => {
  if (isAwaitingLandlordSend(status, expectedPaymentSendDate)) {
    return {
      label: 'Processing',
      colorClass: 'text-[#F59E0B]',
      bgColorClass: 'bg-[rgba(245,158,11,0.10)]',
    };
  }
  switch (status.toLowerCase()) {
    case 'success':
      return {
        label: 'Completed',
        colorClass: 'text-success',
        bgColorClass: 'bg-[rgba(8,155,25,0.10)]',
      };
    case 'failed':
      return {
        label: 'Failed',
        colorClass: 'text-failure',
        bgColorClass: 'bg-[rgba(183,13,13,0.08)]',
      };
    case 'pending':
      return {
        label: 'Pending',
        colorClass: 'text-[#F59E0B]',
        bgColorClass: 'bg-[rgba(245,158,11,0.10)]',
      };
    case 'refund':
      return {
        label: 'Refunded',
        colorClass: 'text-[#F59E0B]',
        bgColorClass: 'bg-[rgba(245,158,11,0.10)]',
      };
    case 'processedasinternalpayment':
      return {
        label: 'Processed as Internal Payment',
        colorClass: 'text-[#F59E0B]',
        bgColorClass: 'bg-[rgba(245,158,11,0.10)]',
      };
    case 'queueforpayment':
    case 'underprocessingforpayment':
      return {
        label: 'In Processing',
        colorClass: 'text-[#F59E0B]',
        bgColorClass: 'bg-[rgba(245,158,11,0.10)]',
      };
    default:
      return {
        label: status.charAt(0).toUpperCase() + status.slice(1),
        colorClass: 'text-[#F59E0B]',
        bgColorClass: 'bg-[rgba(245,158,11,0.10)]',
      };
  }
};
